/**
 * Meals — plan the week, keep the recipe box, shop the list.
 *
 * The whole household uses this (no adult gate). Three views:
 *   Plan     — one meal per day, phone-first, a couple of taps.
 *   Grocery  — generated from the plan, grouped by aisle, checked off in the store.
 *   Meals    — the recipe box with photos and ingredients.
 *
 * The tab and the week live in the URL so a phone can be handed over, or the page
 * refreshed mid-shop, without losing your place.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Aisle, ParsedRecipe, Recipe } from '../../shared/types';
import AisleEditor from './AisleEditor';
import GroceryPanel from './GroceryPanel';
import MealEditor from './MealEditor';
import MealLibrary from './MealLibrary';
import RecipeImport from './RecipeImport';
import RecipeView from './RecipeView';
import WeekPlanner from './WeekPlanner';
import { mondayOf, todayIso } from './dates';
import { errMsg, mealsApi } from './mealsApi';
import { ErrorNote, Loading, btnSecondary } from './ui';

type Tab = 'plan' | 'grocery' | 'library';

const TABS: { id: Tab; label: string }[] = [
  { id: 'plan', label: 'Plan' },
  { id: 'grocery', label: 'Grocery' },
  { id: 'library', label: 'Meals' },
];

function isTab(v: string | null): v is Tab {
  return v === 'plan' || v === 'grocery' || v === 'library';
}

export default function MealsPage() {
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'plan';
  const week = mondayOf(params.get('week') ?? todayIso());

  const [meals, setMeals] = useState<Recipe[] | null>(null);
  const [aisles, setAisles] = useState<Aisle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ meal: Recipe | null; draft?: ParsedRecipe } | null>(null);
  const [viewing, setViewing] = useState<Recipe | null>(null);
  const [importing, setImporting] = useState(false);
  const [showAisles, setShowAisles] = useState(false);
  // Bumped whenever the plan or a meal changes, to re-pull the grocery list.
  const [groceryKey, setGroceryKey] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [m, a] = await Promise.all([mealsApi.listMeals(), mealsApi.listAisles()]);
      setMeals(m);
      setAisles(a);
    } catch (err) {
      setError(errMsg(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  const setWeek = (next: string) => {
    const p = new URLSearchParams(params);
    p.set('week', next);
    setParams(p, { replace: true });
  };

  const loading = meals === null || aisles === null;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Meals</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Plan the week and the grocery list writes itself.
          </p>
        </div>
        {!loading && tab === 'library' && (
          <button type="button" className={btnSecondary} onClick={() => setShowAisles(true)}>
            Aisle order
          </button>
        )}
      </header>

      <div role="tablist" aria-label="Meals views" className="flex gap-1 rounded-full bg-surface-sunk p-1">
        {TABS.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className={`min-h-[44px] flex-1 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage ${
                active
                  ? 'bg-surface text-ink shadow-sm'
                  : 'text-ink-soft hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {error && <ErrorNote message={error} onRetry={load} />}

      {loading ? (
        <Loading label="Loading meals" />
      ) : (
        <>
          {tab === 'plan' && (
            <WeekPlanner
              week={week}
              meals={meals}
              onWeek={setWeek}
              onNewMeal={() => setEditing({ meal: null })}
              onPlanChanged={() => setGroceryKey((k) => k + 1)}
            />
          )}

          {tab === 'grocery' && (
            <GroceryPanel
              key={`${week}-${groceryKey}`}
              week={week}
              aisles={aisles}
              onWeek={setWeek}
              onGoToPlan={() => setTab('plan')}
              onGoToLibrary={() => setTab('library')}
              onEditAisles={() => setShowAisles(true)}
            />
          )}

          {tab === 'library' && (
            <MealLibrary
              meals={meals}
              onNew={() => setEditing({ meal: null })}
              onImport={() => setImporting(true)}
              onOpen={(meal) => setViewing(meal)}
            />
          )}
        </>
      )}

      {viewing && (
        <RecipeView
          recipe={viewing}
          onEdit={() => {
            setEditing({ meal: viewing });
            setViewing(null);
          }}
          onGroceryChanged={() => setGroceryKey((k) => k + 1)}
          onClose={() => setViewing(null)}
        />
      )}

      {importing && (
        <RecipeImport
          onParsed={(recipe) => {
            setImporting(false);
            setEditing({ meal: null, draft: recipe });
          }}
          onClose={() => setImporting(false)}
        />
      )}

      {editing && aisles && (
        <MealEditor
          meal={editing.meal}
          draft={editing.draft}
          aisles={aisles}
          onSaved={() => {
            void load();
            setGroceryKey((k) => k + 1);
          }}
          onDeleted={() => {
            void load();
            setGroceryKey((k) => k + 1);
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {showAisles && aisles && (
        <AisleEditor
          aisles={aisles}
          onChanged={async () => {
            await load();
            setGroceryKey((k) => k + 1);
          }}
          onClose={() => setShowAisles(false)}
        />
      )}
    </div>
  );
}
